/**
 * Contracts view-model (spec §9.3, plan §6 M2-18): the station's board (type, reward, deadline, requirements; the
 * ×1.5 premium variants tagged) and the active jobs with hand-in where it applies here.
 */
import { visibleBoard } from '../../contracts/board'
import { handInCheck, isWorksContract, worksLive, worksNeedT, type WorksView } from '../../contracts/commands'
import { EPSILON, GOOD_NAMES } from '../../career/ops'
import type { ContractOffer } from '../../contracts/generate'
import type { Career, ContractParams, ContractState } from '../../career/types'
import { stationIndexOf } from '../../data/m2'
import { wareIndex, type ContractType, type M2Data, type StationDef, type StationId } from '../../data/m2Schemas'
import { UI_CONFIG, type UiConfig } from '../config'
import { formatCr, formatDuration, formatTonnes } from '../format'
import type { SeedLine } from './seeds'

export interface BoardRow {
  id: string
  type: ContractType
  typeLabel: string
  reward: string
  deadline: string
  requirement: string
  premium: boolean
  standing: string
}

export interface ActiveRow {
  id: string
  type: ContractType
  typeLabel: string
  station: string
  reward: string
  timeLeft: string
  requirement: string
  ready: boolean
  /** Hand-in works here now. */
  canHandIn: boolean
  /** Why it cannot be handed in here (null when it can). */
  handInNote: string | null
}

export interface ContractsModel {
  available: boolean
  board: BoardRow[]
  active: ActiveRow[]
  /** M7 S33 (spec §8.2): a line pinned on this board (the Meridian contracts desk), or null. */
  boardLine: SeedLine | null
}

function stationName(data: M2Data, id: StationId | undefined): string {
  if (id === undefined) return 'a station'
  const i = stationIndexOf(data, id)
  return i < 0 ? id : data.stations.stations[i].name
}

/** What a job asks for, in one line. */
export function requirementText(data: M2Data, type: ContractType, station: StationId, params: ContractParams): string {
  switch (type) {
    case 'delivery': {
      // M8 SNK-6b2: a works delivery names its good (`params.good`, in `cargo.parts`).
      const ware =
        params.good !== undefined ? GOOD_NAMES[params.good] : params.ware === undefined ? 'cargo' : data.wares.wares[wareIndex(params.ware)].name
      return `Deliver ${formatTonnes(params.tons ?? 0)} t of ${ware} to ${stationName(data, station)}`
    }
    case 'recovery': {
      const def = data.wares.components.find((c) => c.type === params.componentType)
      return `Recover a ${def?.name ?? 'component'} from the marked wreck and bring it to ${stationName(data, station)}`
    }
    case 'clearRaiders':
      return 'Destroy the raider group at the marker'
    case 'courier':
      return `Carry a sealed ${formatTonnes(data.contracts.types.courier.podMassT)} t pod to ${stationName(data, params.destination)}`
    // M3 bridge (R22g): the Reach types name their target as the offer captured it.
    case 'laneRepair':
      return `Repair ${params.targetName ?? 'the lane ring'}, then report to ${stationName(data, station)}`
    case 'relayParts': {
      const units = params.units ?? 0
      return `Deliver ${units} relay part${units === 1 ? '' : 's'} to ${params.targetName ?? 'the relay'} (${params.delivered ?? 0}/${units} in)`
    }
    case 'chartSystem':
      return `Chart ${params.targetName ?? params.systemId ?? 'the system'}, then report to ${stationName(data, station)}`
  }
}

/** The deadline of an offer: whole lease cycles read as cycles, anything else as flight time. */
export function deadlineText(seconds: number, cycleSeconds: number): string {
  const cycles = seconds / cycleSeconds
  if (Number.isInteger(cycles) && cycles >= 1) return `${cycles} lease cycle${cycles === 1 ? '' : 's'} (${formatDuration(seconds)})`
  return formatDuration(seconds)
}

function boardRow(data: M2Data, offer: ContractOffer, cycleSeconds: number, cfg: UiConfig): BoardRow {
  return {
    id: offer.id,
    type: offer.type,
    typeLabel: cfg.contractTypes[offer.type],
    reward: formatCr(offer.reward),
    deadline: deadlineText(offer.durationSeconds, cycleSeconds),
    requirement: requirementText(data, offer.type, offer.station, offer.params),
    premium: offer.premium,
    standing: `+${offer.standing}`,
  }
}

/** "Deliver 40 t of Steel to … (the site needs 25 t now)" when a works site's live need is under the contract tonnes. */
function worksRequirement(data: M2Data, c: ContractState, needT: number): string {
  const base = requirementText(data, c.type, c.station, c.params)
  return needT + EPSILON < (c.params.tons ?? 0) ? `${base} (the site needs ${formatTonnes(needT)} t now)` : base
}

function activeRow(data: M2Data, career: Career, here: StationId, c: ContractState, gameTime: number, cfg: UiConfig, view: WorksView | null): ActiveRow {
  // SNK-6b2: a works hand-in is checked against what the site still needs (the runtime's rule), not the full tonnes.
  const live = isWorksContract(c) ? worksLive(view, c) : null
  const check = handInCheck(data, career, here, c.id, live)
  const left = c.deadlineAt - gameTime
  return {
    id: c.id,
    type: c.type,
    typeLabel: cfg.contractTypes[c.type],
    station: stationName(data, c.station),
    reward: formatCr(c.reward),
    timeLeft: left > 0 ? `${formatDuration(left)} left` : 'Expired',
    requirement: live === null ? requirementText(data, c.type, c.station, c.params) : worksRequirement(data, c, worksNeedT(c, live)),
    ready: c.status === 'ready',
    canHandIn: check === null,
    handInNote: check === null ? null : check.message,
  }
}

/** `view`: the strategic client's local system view (works rows' live need; null or absent: the contract tonnes). */
export function contractsModel(
  data: M2Data,
  career: Career,
  here: StationDef,
  gameTime: number,
  cfg: UiConfig = UI_CONFIG,
  view: WorksView | null = null,
): ContractsModel {
  const available = here.services.includes('contracts')
  const cycle = career.lease.cycleSeconds
  return {
    available,
    board: available ? visibleBoard(data, career, here.id).map((o) => boardRow(data, o, cycle, cfg)) : [],
    active: career.contracts.active.map((c) => activeRow(data, career, here.id, c, gameTime, cfg, view)),
    boardLine: available ? (here.desk?.boardLine ?? null) : null,
  }
}
